import { DetailedApiMethod, JSONObject, JSONValue } from '../../types/types';
import { resolveTemplate } from '../../utils/json/resolveTemplate';
import { CustomJsonataFunctions } from '../../utils/json/resolveStringExpression';
import { runResponseActions } from '../../utils/apiRequests/runResponseActions';
import { ResponseActionHandler } from '../../types/context';
import {
  DatasetControlAggregateActionApiRequestData,
  DatasetControlMethodActionApiRequestData,
  DatasetDataApiRequestData,
  FormApiMethodsRequestData,
  ResolvedApiMethod,
  HandlerRequestContext,
  LayoutApiRequestData,
  SubsetControlMethodActionApiRequestData,
} from '../types/types';
import { resolveApiPath } from './resolveApiPath';

export const apiRequest = async <T>(
  apiMethod: DetailedApiMethod,
  jsonData:
    | DatasetDataApiRequestData
    | DatasetControlMethodActionApiRequestData
    | DatasetControlAggregateActionApiRequestData
    | SubsetControlMethodActionApiRequestData
    | FormApiMethodsRequestData
    | LayoutApiRequestData
    | HandlerRequestContext,
  dataRequest: (apiMethod: ResolvedApiMethod) => Promise<T>,
  getResponseActionHandler?: (
    name: string
  ) => ResponseActionHandler | undefined,
  customFunctions?: CustomJsonataFunctions
): Promise<T> => {
  // response and errorResponse are resolved only after the call, so they are
  // kept out of resolveTemplate here.
  const {
    responseActions,
    errorResponse,
    response,
    ...apiMethodWithoutActions
  } = apiMethod;

  // 1) Resolve apiMethod with provided data
  const apiMethodResolved = await resolveTemplate(
    apiMethodWithoutActions,
    jsonData,
    customFunctions
  );

  // 2) Create request data with resolved apiMethod path. errorResponse travels
  // unresolved, bound to the data the fetcher must evaluate it against.
  const apiMethodWithResolvedPath: ResolvedApiMethod = {
    ...apiMethodResolved,
    path: resolveApiPath(apiMethodResolved.path),
    ...(errorResponse !== undefined
      ? {
          errorResponse: {
            template: errorResponse,
            jsonData: jsonData as JSONObject,
            customFunctions,
          },
        }
      : {}),
  };

  // 3) Request data from BE
  const httpResponse = await dataRequest(apiMethodWithResolvedPath);

  // 4) Add response to resolve data
  const responseData = {
    ...jsonData,
    response: httpResponse as JSONValue,
  };

  // 5) Run action handlers if provided
  if (getResponseActionHandler && responseActions) {
    await runResponseActions(
      responseActions,
      responseData,
      getResponseActionHandler
    );
  }

  // 6) return raw data if no response template provided
  if (!response) {
    return httpResponse;
  }

  // 7) Resolve response template and return resolved response
  const resolvedResponse = (await resolveTemplate(
    response,
    responseData,
    customFunctions
  )) as T;

  return resolvedResponse;
};
